import { useState } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  AdminTable,
  AdminModal,
  ConfirmDialog,
  FormField,
  LangTabs,
  Toggle,
  PublishBadge,
  SortOrderInput,
  LoadingState,
  ErrorState,
  ApiModeBanner,
  inputCls,
} from "../components/AdminUI";
import { useAdminData } from "../../../store/adminDataStore";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import type { Service, ServiceOption } from "../../../store/types";

const NOW = () => new Date().toISOString();
const CITIES_ALL = ["kyoto", "osaka", "tokyo"];

const EMPTY_SVC: Service = {
  id: "", service_code: "",
  title_zh: "", title_en: "", title_ja: "",
  short_desc_zh: "", short_desc_en: "", short_desc_ja: "",
  base_price_twd: 0, duration_hours: 8,
  city_scope: ["kyoto"],
  cover_image_url: "", icon: "ri-camera-line",
  features: [{ zh: "", en: "", ja: "" }, { zh: "", en: "", ja: "" }, { zh: "", en: "", ja: "" }],
  is_active: true, sort_order: 99,
  created_at: NOW(), updated_at: NOW(),
};

const EMPTY_OPT: Omit<ServiceOption, "service_id"> = {
  id: "", option_code: "",
  title_zh: "", title_en: "", title_ja: "",
  price_delta_twd: 0, is_active: true, sort_order: 99,
};

export default function AdminServicesPage() {
  const { state, upsertService, removeService, upsertServiceOption, removeServiceOption, status, refetchServices, isApiMode } = useAdminData();
  const t = useAdminT();
  const { showToast } = useToast();
  const services = [...state.services].sort((a, b) => a.sort_order - b.sort_order);

  const [editSvc, setEditSvc] = useState<Service | null>(null);
  const [isNewSvc, setIsNewSvc] = useState(false);
  const [svcLang, setSvcLang] = useState<"zh" | "en" | "ja">("zh");
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [optPanel, setOptPanel] = useState<string | null>(null);
  const [editOpt, setEditOpt] = useState<ServiceOption | null>(null);
  const [isNewOpt, setIsNewOpt] = useState(false);
  const [optLang, setOptLang] = useState<"zh" | "en" | "ja">("zh");

  const openAddSvc = () => { setEditSvc({ ...EMPTY_SVC, id: `svc-${Date.now()}` }); setIsNewSvc(true); };
  const openEditSvc = (s: Service) => { setEditSvc({ ...s, features: s.features.map(f => ({ ...f })) }); setIsNewSvc(false); };

  const saveSvc = async () => {
    if (!editSvc) return;
    setSaving(true);
    try {
      await upsertService({ ...editSvc, updated_at: NOW() });
      showToast(t("tabifotoAdmin.common.save_success"), "success");
      setEditSvc(null);
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    } finally {
      setSaving(false);
    }
  };

  const setSvcField = <K extends keyof Service>(k: K, v: Service[K]) => {
    if (!editSvc) return;
    setEditSvc({ ...editSvc, [k]: v });
  };

  const setSvcLangField = (field: "title" | "short_desc", l: "zh" | "en" | "ja", v: string) => {
    if (!editSvc) return;
    setEditSvc({ ...editSvc, [`${field}_${l}`]: v } as Service);
  };

  const setFeature = (idx: number, v: string) => {
    if (!editSvc) return;
    const features = editSvc.features.map((f, i) => i === idx ? { ...f, [svcLang]: v } : f);
    setEditSvc({ ...editSvc, features });
  };

  const handleToggleActive = async (svc: Service, v: boolean) => {
    try {
      await upsertService({ ...svc, is_active: v, updated_at: NOW() });
      showToast(t("tabifotoAdmin.common.save_success"), "success");
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    }
  };

  const handleDeactivate = async (id: string) => {
    const svc = services.find(s => s.id === id);
    if (!svc) return;
    try {
      await upsertService({ ...svc, is_active: false });
      showToast(t("tabifotoAdmin.common.save_success"), "success");
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    }
    setConfirmDel(null);
  };

  const optionsForService = (sid: string) =>
    [...state.serviceOptions].filter(o => o.service_id === sid).sort((a, b) => a.sort_order - b.sort_order);

  const openAddOpt = (sid: string) => {
    setEditOpt({ ...EMPTY_OPT, id: `opt-${Date.now()}`, service_id: sid });
    setIsNewOpt(true);
  };

  const openEditOpt = (o: ServiceOption) => { setEditOpt({ ...o }); setIsNewOpt(false); };

  const saveOpt = async () => {
    if (!editOpt) return;
    setSaving(true);
    try {
      await upsertServiceOption(editOpt);
      showToast(t("tabifotoAdmin.common.save_success"), "success");
      setEditOpt(null);
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    } finally {
      setSaving(false);
    }
  };

  const setOptField = <K extends keyof ServiceOption>(k: K, v: ServiceOption[K]) => {
    if (!editOpt) return;
    setEditOpt({ ...editOpt, [k]: v });
  };

  const setOptLangField = (l: "zh" | "en" | "ja", v: string) => {
    if (!editOpt) return;
    setEditOpt({ ...editOpt, [`title_${l}`]: v } as ServiceOption);
  };

  const handleRemoveOpt = async (id: string) => {
    try {
      await removeServiceOption(id);
      showToast(t("tabifotoAdmin.common.delete_success"), "success");
    } catch {
      showToast(t("tabifotoAdmin.common.delete_error"), "error");
    }
  };

  const HEADERS = [
    t("tabifotoAdmin.services.col_order"),
    t("tabifotoAdmin.services.col_image"),
    t("tabifotoAdmin.services.col_code"),
    t("tabifotoAdmin.services.col_title"),
    t("tabifotoAdmin.services.col_price"),
    t("tabifotoAdmin.services.col_hours"),
    t("tabifotoAdmin.services.col_cities"),
    t("tabifotoAdmin.services.col_active"),
    t("tabifotoAdmin.services.col_sort"),
    t("tabifotoAdmin.services.col_options"),
    t("tabifotoAdmin.services.col_actions"),
  ];

  return (
    <AdminLayout
      title={t("tabifotoAdmin.services.title")}
      subtitle={t("tabifotoAdmin.services.subtitle")}
      action={
        <button onClick={openAddSvc} className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#2C2C2C] text-white text-sm hover:bg-[#2C2C2C]/80 cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
          {t("tabifotoAdmin.services.add_service")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      {status.services.loading && <LoadingState />}
      {status.services.error && <ErrorState message={status.services.error} onRetry={refetchServices} />}

      {!status.services.loading && !status.services.error && (
        <AdminTable headers={HEADERS} empty={services.length === 0}>
          {services.map(svc => (
            <tr key={svc.id} className="border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60">
              <td className="px-4 py-3 text-xs text-[#2C2C2C]/30">{svc.sort_order}</td>
              <td className="px-4 py-3">
                <div className="w-14 h-10 rounded overflow-hidden bg-[#D9D9D9]/30">
                  {svc.cover_image_url && <img src={svc.cover_image_url} alt="" className="w-full h-full object-cover" />}
                </div>
              </td>
              <td className="px-4 py-3 text-xs font-mono text-[#2C2C2C]/40">{svc.service_code}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center text-[#C8B8A6] shrink-0"><i className={svc.icon} /></div>
                  <span className="text-sm font-light text-[#2C2C2C]">{svc.title_zh}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-sm font-light text-[#2C2C2C]">
                {svc.base_price_twd > 0 ? `NT$${svc.base_price_twd.toLocaleString()}` : "—"}
              </td>
              <td className="px-4 py-3 text-xs text-[#2C2C2C]/50">{svc.duration_hours}h</td>
              <td className="px-4 py-3">
                <div className="flex gap-1 flex-wrap">
                  {svc.city_scope.map(c => (
                    <span key={c} className="text-[10px] bg-[#C8B8A6]/20 text-[#C8B8A6] px-1.5 py-0.5 rounded-full capitalize">{c}</span>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3">
                <Toggle checked={svc.is_active} onChange={v => handleToggleActive(svc, v)} />
              </td>
              <td className="px-4 py-3">
                <SortOrderInput order={svc.sort_order} onChange={v => upsertService({ ...svc, sort_order: v, updated_at: NOW() })} />
              </td>
              <td className="px-4 py-3">
                <button onClick={() => setOptPanel(optPanel === svc.id ? null : svc.id)}
                  className="flex items-center gap-1 text-xs text-[#C8B8A6] hover:text-[#2C2C2C] cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-settings-3-line" /></div>
                  {optionsForService(svc.id).length} opts
                </button>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <button onClick={() => openEditSvc(svc)} className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/40 hover:text-[#2C2C2C] cursor-pointer"><i className="ri-edit-line" /></button>
                  <button onClick={() => setConfirmDel(svc.id)} className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/30 hover:text-red-400 cursor-pointer"><i className="ri-delete-bin-line" /></button>
                </div>
              </td>
            </tr>
          ))}
        </AdminTable>
      )}

      {/* Service Options panel */}
      {optPanel && (() => {
        const svc = services.find(s => s.id === optPanel);
        if (!svc) return null;
        const opts = optionsForService(optPanel);
        return (
          <div className="mt-4 bg-white rounded-lg border border-[#C8B8A6]/30 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-[#C8B8A6]/10 border-b border-[#C8B8A6]/20">
              <p className="text-xs font-light text-[#2C2C2C]/60 uppercase tracking-wider">service_options → {svc.title_zh}</p>
              <button onClick={() => openAddOpt(optPanel)} className="flex items-center gap-1 text-xs text-[#C8B8A6] hover:text-[#2C2C2C] cursor-pointer whitespace-nowrap">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
                {t("tabifotoAdmin.services.add_option")}
              </button>
            </div>
            {opts.length === 0 ? (
              <p className="text-center py-6 text-xs text-[#2C2C2C]/30">{t("tabifotoAdmin.services.no_options")}</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[#D9D9D9]/40 bg-[#F7F6F3]">
                  {[t("tabifotoAdmin.services.option_code"), t("tabifotoAdmin.services.option_title"), "EN", t("tabifotoAdmin.services.price_delta"), t("tabifotoAdmin.common.active"), t("tabifotoAdmin.common.sort_order"), ""].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-xs uppercase tracking-wider text-[#2C2C2C]/30 font-light">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {opts.map(opt => (
                    <tr key={opt.id} className="border-b border-[#D9D9D9]/30 hover:bg-[#F7F6F3]/40">
                      <td className="px-4 py-2 text-xs font-mono text-[#2C2C2C]/40">{opt.option_code}</td>
                      <td className="px-4 py-2 text-xs text-[#2C2C2C]/70">{opt.title_zh}</td>
                      <td className="px-4 py-2 text-xs text-[#2C2C2C]/50">{opt.title_en}</td>
                      <td className="px-4 py-2 text-xs text-[#2C2C2C]">+NT${opt.price_delta_twd.toLocaleString()}</td>
                      <td className="px-4 py-2"><Toggle checked={opt.is_active} onChange={v => upsertServiceOption({ ...opt, is_active: v })} /></td>
                      <td className="px-4 py-2"><SortOrderInput order={opt.sort_order} onChange={v => upsertServiceOption({ ...opt, sort_order: v })} /></td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1">
                          <button onClick={() => openEditOpt(opt)} className="w-6 h-6 flex items-center justify-center text-[#2C2C2C]/40 hover:text-[#2C2C2C] cursor-pointer"><i className="ri-edit-line text-xs" /></button>
                          <button onClick={() => handleRemoveOpt(opt.id)} className="w-6 h-6 flex items-center justify-center text-[#2C2C2C]/30 hover:text-red-400 cursor-pointer"><i className="ri-delete-bin-line text-xs" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })()}

      {/* Service Edit Modal */}
      {editSvc && (
        <AdminModal
          title={isNewSvc ? t("tabifotoAdmin.services.add_service") : `${t("tabifotoAdmin.services.edit_service")}: ${editSvc.title_zh || editSvc.service_code}`}
          onClose={() => setEditSvc(null)} onSave={saveSvc} saving={saving}
        >
          <div className="grid grid-cols-2 gap-3 mb-3">
            <FormField label={t("tabifotoAdmin.services.service_code")} required>
              <input className={inputCls} value={editSvc.service_code} onChange={e => setSvcField("service_code", e.target.value)} placeholder="photography_8h" />
            </FormField>
            <FormField label={t("tabifotoAdmin.services.icon_class")}>
              <input className={inputCls} value={editSvc.icon} onChange={e => setSvcField("icon", e.target.value)} placeholder="ri-camera-line" />
            </FormField>
          </div>

          <LangTabs activeLang={svcLang} onChange={setSvcLang} />
          <div className="mt-3">
            <FormField label={`${t("tabifotoAdmin.services.title_field")} (${svcLang})`} required>
              <input className={inputCls}
                value={(editSvc as Record<string, unknown>)[`title_${svcLang}`] as string}
                onChange={e => setSvcLangField("title", svcLang, e.target.value)} />
            </FormField>
            <FormField label={`${t("tabifotoAdmin.services.short_desc")} (${svcLang})`}>
              <textarea className={`${inputCls} resize-none`} rows={3} maxLength={500}
                value={(editSvc as Record<string, unknown>)[`short_desc_${svcLang}`] as string}
                onChange={e => setSvcLangField("short_desc", svcLang, e.target.value)} />
            </FormField>
            <FormField label={t("tabifotoAdmin.services.features")}>
              {editSvc.features.map((feat, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input className={`${inputCls} flex-1`} value={feat[svcLang]}
                    onChange={e => setFeature(idx, e.target.value)} placeholder={`Feature ${idx + 1}`} />
                  <button onClick={() => setEditSvc({ ...editSvc, features: editSvc.features.filter((_, i) => i !== idx) })}
                    className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/30 hover:text-red-400 cursor-pointer shrink-0"><i className="ri-close-line" /></button>
                </div>
              ))}
              <button onClick={() => setEditSvc({ ...editSvc, features: [...editSvc.features, { zh: "", en: "", ja: "" }] })}
                className="text-xs text-[#C8B8A6] hover:text-[#2C2C2C] cursor-pointer">{t("tabifotoAdmin.services.add_feature")}</button>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("tabifotoAdmin.services.base_price")}>
              <input type="number" className={inputCls} value={editSvc.base_price_twd} onChange={e => setSvcField("base_price_twd", Number(e.target.value))} />
            </FormField>
            <FormField label={t("tabifotoAdmin.services.duration_hours")}>
              <input type="number" className={inputCls} value={editSvc.duration_hours} onChange={e => setSvcField("duration_hours", Number(e.target.value))} />
            </FormField>
          </div>

          <FormField label={t("tabifotoAdmin.services.city_scope")}>
            <div className="flex gap-3 mt-1">
              {CITIES_ALL.map(c => (
                <label key={c} className="flex items-center gap-1.5 text-sm text-[#2C2C2C]/60 cursor-pointer capitalize">
                  <input type="checkbox" className="w-3.5 h-3.5 accent-[#C8B8A6]"
                    checked={editSvc.city_scope.includes(c)}
                    onChange={e => setSvcField("city_scope", e.target.checked ? [...editSvc.city_scope, c] : editSvc.city_scope.filter(x => x !== c))} />
                  {c}
                </label>
              ))}
            </div>
          </FormField>

          <FormField label={t("tabifotoAdmin.services.cover_image")}>
            <input className={inputCls} value={editSvc.cover_image_url} onChange={e => setSvcField("cover_image_url", e.target.value)} placeholder="https://..." />
            {editSvc.cover_image_url && (
              <div className="mt-2 w-full h-24 rounded overflow-hidden"><img src={editSvc.cover_image_url} alt="" className="w-full h-full object-cover" /></div>
            )}
          </FormField>

          <div className="grid grid-cols-3 gap-3 mt-2">
            <FormField label={t("tabifotoAdmin.common.sort_order")}><input type="number" className={inputCls} value={editSvc.sort_order} onChange={e => setSvcField("sort_order", Number(e.target.value))} /></FormField>
            <FormField label={t("tabifotoAdmin.common.active")}><div className="flex items-center h-9"><Toggle checked={editSvc.is_active} onChange={v => setSvcField("is_active", v)} /></div></FormField>
          </div>
        </AdminModal>
      )}

      {/* Service Option Modal */}
      {editOpt && (
        <AdminModal
          title={isNewOpt ? t("tabifotoAdmin.services.add_option") : t("tabifotoAdmin.services.edit_option")}
          onClose={() => setEditOpt(null)} onSave={saveOpt} saving={saving}
        >
          <FormField label={t("tabifotoAdmin.services.option_code")} required>
            <input className={inputCls} value={editOpt.option_code} onChange={e => setOptField("option_code", e.target.value)} placeholder="extra_4h" />
          </FormField>
          <LangTabs activeLang={optLang} onChange={setOptLang} />
          <div className="mt-3">
            <FormField label={`${t("tabifotoAdmin.services.option_title")} (${optLang})`}>
              <input className={inputCls} value={(editOpt as Record<string, unknown>)[`title_${optLang}`] as string}
                onChange={e => setOptLangField(optLang, e.target.value)} />
            </FormField>
          </div>
          <FormField label={t("tabifotoAdmin.services.price_delta")}>
            <input type="number" className={inputCls} value={editOpt.price_delta_twd} onChange={e => setOptField("price_delta_twd", Number(e.target.value))} />
          </FormField>
          <div className="flex gap-6 mt-2">
            <label className="flex items-center gap-2 text-sm text-[#2C2C2C]/60 cursor-pointer">
              <Toggle checked={editOpt.is_active} onChange={v => setOptField("is_active", v)} />
              {t("tabifotoAdmin.common.active")}
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#2C2C2C]/40">{t("tabifotoAdmin.common.sort_order")}</span>
              <input type="number" className="w-16 px-2 py-1 rounded border border-[#D9D9D9] text-xs text-center focus:outline-none focus:border-[#C8B8A6]"
                value={editOpt.sort_order} onChange={e => setOptField("sort_order", Number(e.target.value))} />
            </div>
          </div>
        </AdminModal>
      )}

      {/* Confirm Deactivate */}
      {confirmDel && (
        <ConfirmDialog
          title={t("tabifotoAdmin.services.deactivate_confirm")}
          message={t("tabifotoAdmin.services.deactivate_desc")}
          confirmLabel={t("tabifotoAdmin.common.deactivate")}
          onConfirm={() => handleDeactivate(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </AdminLayout>
  );
}
